import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Scale, Users, TrendingUp } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const libraryTopics = [
  {
    icon: Scale,
    title: "Authority vs Attention",
    description: "Why attention-based positioning fails under buyer scrutiny and how authority infrastructure creates lasting value.",
    category: "Foundation",
  },
  {
    icon: Users,
    title: "Founder Risk & Substitution",
    description: "How founder dependency creates key-person risk and the framework for systematic founder substitution.",
    category: "Governance",
  },
  {
    icon: TrendingUp,
    title: "Licensing vs Consulting",
    description: "The economic and operational differences between time-based consulting and scalable licensing models.",
    category: "Economics",
  },
  {
    icon: BookOpen,
    title: "Buyer Readiness Architecture",
    description: "What buyers look for in diligence and how to build infrastructure that answers questions before they're asked.",
    category: "Exit",
  },
];

const AuthorityLibraryPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="authority-library-page">
      <SEOHead 
        title="Authority Library - Evergreen Concepts"
        description="A controlled repository of authority concepts supporting SEO, operator education, and buyer diligence."
        url="https://onpointauthority.com/library"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="library-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Evergreen Content Hub
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Authority<br />
              <span className="text-primary">Library</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              A controlled repository of authority concepts, not a blog. Supports SEO, operator 
              education, and buyer diligence without promotion.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Topics Grid */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="topics-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {libraryTopics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-8 hover:border-primary/30 transition-colors"
                data-testid={`topic-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <topic.icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
                  <span className="text-xs font-mono text-muted-foreground">{topic.category}</span>
                </div>
                <h3 className="font-serif text-2xl text-foreground mb-3">{topic.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{topic.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Purpose Statement */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="purpose-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Usage
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-foreground mb-8">
              Education, Not<br />
              <span className="text-primary">Promotion</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground mb-10">
              These concepts exist to clarify authority infrastructure for operators and buyers. 
              They are not sales content. They are reference material.
            </p>
            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="library-cta"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AuthorityLibraryPage;
